/**
 * Coupon API helpers: token header and 403 (invalid/expired token) handling.
 * Used for IDOR-safe coupon operations after step/init.
 */

export const COUPON_TOKEN_HEADER = 'X-Coupon-Token';

/** Pop-in draft / payment APIs (mirrors coupon token pattern) */
export const POPIN_TOKEN_HEADER = 'X-Popin-Token';
export const POPIN_MANAGE_TOKEN_HEADER = 'X-Popin-Manage-Token';

export const POPIN_TOKEN_INVALID_MESSAGE =
  'This pop-in link is invalid or expired. Please start again from the beginning.';

/** User-facing message when token is invalid or expired */
export const COUPON_TOKEN_INVALID_MESSAGE =
  'This coupon link is invalid or expired. Please start again from the beginning.';

/** API error code for invalid or missing coupon access token */
export const COUPON_TOKEN_ERROR_CODE = '4031';

/**
 * Build headers for coupon API requests, including creator token when available.
 * @param {string|null|undefined} creatorToken
 * @param {Record<string, string>} [baseHeaders]
 * @returns {Record<string, string>}
 */
export function couponApiHeaders(creatorToken, baseHeaders = {}) {
  const headers = { ...baseHeaders };
  if (creatorToken) {
    headers[COUPON_TOKEN_HEADER] = creatorToken;
  }
  return headers;
}

/**
 * Check if a response is 403 with code 4031 / "Invalid or missing coupon access token".
 * @param {Response} res
 * @param {{ code?: string|number; error?: string }} [body]
 * @returns {boolean}
 */
export function isCouponTokenForbidden(res, body = {}) {
  if (res.status !== 403) return false;
  const code = body.code != null ? String(body.code) : null;
  const error = (body.error || '').toLowerCase();
  return (
    code === COUPON_TOKEN_ERROR_CODE ||
    error.includes('invalid or missing coupon access token')
  );
}

/**
 * @param {string|null|undefined} popinCreatorToken
 * @param {Record<string, string>} [baseHeaders]
 */
export function popinApiHeaders(popinCreatorToken, baseHeaders = {}) {
  const headers = { ...baseHeaders };
  if (popinCreatorToken) {
    headers[POPIN_TOKEN_HEADER] = popinCreatorToken;
  }
  return headers;
}

/**
 * Attach a pop-in manage token header to a headers object.
 * Intended for renew-from-email flow and bundled coupon step edits.
 * @param {Record<string, string>} baseHeaders
 * @param {string|null|undefined} popinManageToken
 */
export function withPopinManageToken(baseHeaders = {}, popinManageToken) {
  const headers = { ...(baseHeaders || {}) };
  if (popinManageToken) {
    headers[POPIN_MANAGE_TOKEN_HEADER] = popinManageToken;
  }
  return headers;
}

export function isPopinTokenForbidden(res, body = {}) {
  if (res.status !== 403) return false;
  const code = body.code != null ? String(body.code) : null;
  const error = (body.error || '').toLowerCase();
  return (
    code === COUPON_TOKEN_ERROR_CODE ||
    error.includes('invalid or missing pop-in access token')
  );
}
